// Deduplica su due livelli:
// 1. identità dell'oggetto (WeakSet): lo stesso `Error` che risale più handler
//    viene inviato una volta sola, per sempre — equivale alla ivar
//    `@__closeyourit_captured` del gem e all'`Expando` del dart;
// 2. finestra temporale su chiave (type + message + frame di crash): raffiche
//    identiche entro [windowMs] vengono scartate (es. un onerror in loop) —
//    copre i casi senza oggetto (stringhe di window.onerror).

export class Deduplicator {
  private readonly seen = new Map<string, number>()
  private readonly captured = new WeakSet<object>()

  constructor(
    private readonly windowMs: number = 5000,
    private readonly now: () => number = () => Date.now(),
  ) {}

  /// `true` se [error] (oggetto) è già stato catturato. I primitivi non sono
  /// tracciabili per identità → sempre `false` (ci pensa la finestra).
  wasCaptured(error: unknown): boolean {
    return isObject(error) && this.captured.has(error)
  }

  /// Marca [error] come catturato (no-op sui primitivi).
  markCaptured(error: unknown): void {
    if (isObject(error)) this.captured.add(error)
  }

  /// `true` se [key] è già stata vista entro la finestra; altrimenti la registra.
  isDuplicate(key: string): boolean {
    const current = this.now()
    this.prune(current)
    const previous = this.seen.get(key)
    if (previous !== undefined && current - previous < this.windowMs) {
      return true
    }
    this.seen.set(key, current)
    return false
  }

  private prune(current: number): void {
    for (const [key, ts] of this.seen) {
      if (current - ts >= this.windowMs) this.seen.delete(key)
    }
  }
}

function isObject(value: unknown): value is object {
  return (typeof value === 'object' && value !== null) || typeof value === 'function'
}

/// Chiave di dedup da tipo, messaggio e primo frame (top of stack = crash).
export function dedupKey(
  type: string,
  message: string,
  topFrame: string | undefined,
): string {
  return `${type}|${message}|${topFrame ?? ''}`
}
